import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Camera, ClipboardList, Users, Printer, Plus, Receipt } from "lucide-react";
import { api } from "../../lib/api";

export default function AdminBusinessHome() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/admin/business-summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  if (!summary) {
    return <p className="font-mono text-sm tracking-[0.3em] text-gray-500">LOADING...</p>;
  }

  const cards = [
    { label: "今日の予定", value: summary.today_schedules.length, to: "/admin/schedules", icon: CalendarDays, testid: "biz-card-today" },
    { label: "確認待ちの写真", value: summary.pending_photos, to: "/admin/site-photos", icon: Camera, testid: "biz-card-photos", highlight: summary.pending_photos > 0 },
    { label: "未入金の請求書", value: summary.unpaid_invoices, to: "/admin/invoices", icon: Receipt, testid: "biz-card-unpaid", highlight: summary.unpaid_invoices > 0 },
    { label: "進行中の案件", value: summary.active_projects, to: "/admin/projects", icon: ClipboardList, testid: "biz-card-projects" },
    { label: "今週の予定", value: summary.week_schedules, to: "/admin/schedules", icon: CalendarDays, testid: "biz-card-week" },
    { label: "有効なスタッフ", value: summary.active_staff, to: "/admin/staff", icon: Users, testid: "biz-card-staff" },
  ];

  const TYPE_LABELS = { work: "作業", off: "休日", leave: "休暇" };

  return (
    <div data-testid="admin-business-home">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-mincho text-2xl font-bold text-white">業務ホーム</h1>
          <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">BUSINESS HOME — 今日の仕事がひと目で分かります</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="biz-print-today" onClick={() => window.print()} className="flex items-center gap-2 border border-line px-4 py-2.5 text-xs text-gray-300 transition-colors hover:border-accent hover:text-accent">
            <Printer className="h-4 w-4" />
            今日の予定を印刷
          </button>
          <Link to="/admin/schedules" data-testid="biz-add-schedule" className="flex items-center gap-2 bg-accent px-4 py-2.5 font-display text-xs font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover">
            <Plus className="h-4 w-4" />
            予定を登録
          </Link>
        </div>
      </div>

      <Link data-testid="biz-workflow-start" to="/admin/workflow" className="mt-8 flex flex-wrap items-center justify-between gap-3 border-l-4 border-accent bg-panel p-5 transition-colors hover:bg-coal">
        <span className="text-lg font-bold text-white">受付から入金まで</span>
        <span className="text-sm text-accent">電話受付・案件の続きを開く →</span>
      </Link>

      <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-6">
        {cards.map((c) => (
          <Link key={c.testid} to={c.to} data-testid={c.testid} className={`border p-4 transition-colors hover:border-accent ${c.highlight ? "border-accent/60 bg-accent/5" : "border-line bg-panel"}`}>
            <c.icon className={`h-5 w-5 ${c.highlight ? "text-accent" : "text-gray-500"}`} />
            <p className="mt-3 font-display text-3xl font-bold text-white">{c.value}</p>
            <p className="mt-1 text-[11px] text-gray-500">{c.label}</p>
          </Link>
        ))}
      </div>

      <div id="print-area" className="mt-8 border border-line">
        <div className="flex items-center justify-between border-b border-line bg-coal px-4 py-3">
          <h2 className="font-mincho text-base font-bold text-white">今日の予定（{summary.today}）</h2>
          <span className="font-mono text-[10px] tracking-[0.2em] text-gray-500">株式会社ネオ建設</span>
        </div>
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-line font-mono text-[10px] tracking-[0.2em] text-gray-500">
              <th className="px-4 py-2.5">時間</th>
              <th className="px-4 py-2.5">内容</th>
              <th className="px-4 py-2.5">案件 / 現場</th>
              <th className="px-4 py-2.5">担当スタッフ</th>
            </tr>
          </thead>
          <tbody>
            {summary.today_schedules.map((s) => (
              <tr key={s.id} data-testid={`biz-today-row-${s.id}`} className="border-b border-line last:border-b-0">
                <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-gray-400">{s.all_day ? "終日" : `${s.start_time || ""} - ${s.end_time || ""}`}</td>
                <td className="px-4 py-2.5 text-xs font-bold text-white">{s.title}<span className="ml-2 font-normal text-gray-500">{TYPE_LABELS[s.type] || ""}</span></td>
                <td className="px-4 py-2.5 text-xs text-gray-400">{s.project_name}{s.site_name ? ` / ${s.site_name}` : ""}</td>
                <td className="px-4 py-2.5 text-xs text-gray-400">{(s.staff_names || []).filter(Boolean).join("、") || "—"}</td>
              </tr>
            ))}
            {summary.today_schedules.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-500">今日の予定はありません。</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
